import { IMAGE_EDITOR_MENUS } from '~/constants';
import { then } from '~/utils/monadUtil';
import { get, isFunction } from 'lodash';
import { action, computed, observable } from 'mobx';
import { curry, juxt, merge, objOf, pipe, prop, tap } from 'ramda';
import TuiImageEditor from 'tui-image-editor';

type ImageEditorObject = {
  fontSize?: string;
  text?: string;
  type: string;
};

const textPropertyKeys = [
  'fill',
  'fontSize',
  'fontStyle',
  'fontWeight',
  'rotate',
  'angle',
  'textAlign',
  'textDecoration',
];

const defaultTextOption = {
  fontSize: 40,
  fontStyle: 'normal',
  fontWeight: 'normal',
  textAlign: 'left',
  textDecoration: 'none',
};

class ImageEditorStore {
  @observable
  activeMenu: string = IMAGE_EDITOR_MENUS.EDIT.LINE;

  @observable
  activeObjectId = 0;

  @observable
  drawingMode = '';

  @observable
  drawingOption: { width?: number; color?: string } = {};

  @observable
  textOption: object = {};

  @observable
  imageObjectUrl = '';

  @observable
  imageName = '';

  @observable
  angle = 0;

  @observable
  flippedX = false;

  @observable
  flippedY = false;

  @observable
  undoStackLength = 0;

  @observable
  redoStackLength = 0;

  @observable.ref
  imageEditor: TuiImageEditor = null;

  getObjectProps = curry((id, properties) => {
    return this.imageEditor.getObjectProperties(id, properties);
  });

  getActiveObjectProps = (properties) => {
    return this.activeObjectId === 0 ? {} : this.getObjectProps(this.activeObjectId, properties);
  };

  getObjectTextProps = (id) => {
    return this.getObjectProps(id, textPropertyKeys);
  };

  getDataURL = (): string => {
    return this.imageEditor.toDataURL();
  };

  getDrawingMode = () => {
    return this.imageEditor.getDrawingMode();
  };

  @computed
  get isFlipped() {
    return this.flippedX || this.flippedY;
  }

  @computed
  get isRotatedOrFlipped() {
    return this.isFlipped || this.angle > 0;
  }

  isEdited = () => {
    return !this.imageEditor.isEmptyUndoStack();
  };

  @action
  addText = ({ originPosition: position }) => {
    this.imageEditor.addText('Double Click', { position });
  };

  @action
  clearUndoStack = () => {
    this.imageEditor?.clearUndoStack();
  };

  @action
  crop = () => {
    pipe(
      (cropzone: any) => this.imageEditor.crop(cropzone),
      then(this.stopDrawingMode)
    )(this.imageEditor.getCropzoneRect());
  };

  @action
  deactivate = () => {
    this.setActiveObjectId(0);
    this.setTextOption(defaultTextOption);
  };

  @action
  destroy = () => {
    if (isFunction(this.imageEditor?.destroy)) {
      this.imageEditor.destroy();
      this.imageEditor = null;
    }
    this.activeMenu = IMAGE_EDITOR_MENUS.EDIT.LINE;
    this.activeObjectId = 0;
    this.drawingMode = '';
    this.drawingOption = {};
    this.textOption = {};
    this.imageObjectUrl = '';
    this.imageName = '';
    this.angle = 0;
    this.flippedX = false;
    this.flippedY = false;
    this.undoStackLength = 0;
    this.redoStackLength = 0;
  };

  @action
  flipX = () => {
    this.imageEditor.flipX().then(({ flipX, flipY }) => {
      this.setFlipOption(flipX, flipY);
    });
  };

  @action
  flipY() {
    this.imageEditor.flipY().then(({ flipX, flipY }) => {
      this.setFlipOption(flipX, flipY);
    });
  }

  @action
  loadImageFromURL = async (url, name) => {
    await this.imageEditor.loadImageFromURL(url, name);
    this.setImageObjectUrl(url);
    this.setImageName(name);
    this.clearUndoStack();
  };

  @action
  reset = () => {
    this.loadImageFromURL(this.imageObjectUrl, this.imageName);
  };

  @action
  resetRotateAndFlip = async () => {
    await this.setAngle(0);
    this.setAngleOption(0);
    if (this.isFlipped) {
      await this.imageEditor.resetFlip();
      this.setFlipOption(false, false);
    }
  };

  @action
  rotateRight = async () => {
    this.setAngleOption(await this.imageEditor.rotate(90));
  };

  @action
  rotateLeft = async () => {
    this.setAngleOption(await this.imageEditor.rotate(-90));
  };

  private handleObjectActivated = (object) => {
    setTimeout(() => {
      pipe(prop('id'), tap(this.setActiveObjectId), this.getObjectTextProps, this.setTextOption)(object);
    }, 0);
  };

  private handleObjectScaled = (imageEditorObject: ImageEditorObject) => {
    if (imageEditorObject.type !== 'text') return;
    this.setTextOption({ fontSize: parseInt(imageEditorObject.fontSize, 10) });
  };

  @action
  set = (imageEditor) => {
    this.imageEditor = imageEditor;
    this.imageEditor.on('addText', this.addText);
    this.imageEditor.on('mousedown', this.deactivate);
    this.imageEditor.on('objectActivated', this.handleObjectActivated);
    this.imageEditor.on('objectScaled', this.handleObjectScaled);
    this.imageEditor.on('redoStackChanged', this.setRedoStackLength);
    this.imageEditor.on('undoStackChanged', this.setUndoStackLength);
  };

  @action
  setActiveObjectId = (id) => {
    this.activeObjectId = id;
  };

  @action
  setActiveMenu = (mode) => {
    this.activeMenu = mode;
  };

  @action
  setAngle = (angle) => {
    return this.imageEditor.setAngle(angle);
  };

  @action
  setAngleOption = (angle) => {
    this.angle = ((angle % 360) + 360) % 360;
  };

  @action
  setFlipOption = (flippedX = this.flippedX, flippedY = this.flippedY) => {
    this.flippedX = flippedX;
    this.flippedY = flippedY;
  };

  @action
  setDrawingMode = (mode: string, options?: { width?: number; color?: string }) => {
    this.stopDrawingMode();
    this.imageEditor.startDrawingMode(mode, options);
    this.drawingMode = mode;
  };

  @action
  setDrawingOption = (option: { width?: number; color?: string }) => {
    this.drawingOption = merge(this.drawingOption, option);
    this.setDrawingMode(this.drawingMode, this.drawingOption);
  };

  @action
  stopDrawingMode = () => {
    this.imageEditor.stopDrawingMode();
  };

  @action
  setImageName = (name) => {
    this.imageName = name;
  };

  @action
  setImageObjectUrl = (url) => {
    this.imageObjectUrl = url;
  };

  @action
  setTextStyle = (style) => {
    if (this.activeObjectId === 0) return;
    this.imageEditor.changeTextStyle(this.activeObjectId, style);
  };

  @action
  setTextOption = (style) => {
    this.textOption = merge(this.textOption, style);
  };

  @action
  setRedoStackLength = (length) => {
    this.redoStackLength = length;
  };

  @action
  setUndoStackLength = (length) => {
    this.undoStackLength = length;
  };

  @action
  toggleTextOption = (key, value) => {
    pipe(
      tap(this.setTextStyle),
      this.setTextOption
    )(objOf(key, get(this.textOption, key) === value ? defaultTextOption[key] : value));
  };

  @action
  redo = () => {
    this.imageEditor.redo();
  };

  @action
  undo = () => {
    this.imageEditor.undo();
  };

  @action
  setDrawingFreeMode = () => {
    this.setDrawingMode('FREE_DRAWING', this.drawingOption);
  };

  @action
  setDrawingLineMode = () => {
    this.setDrawingMode('LINE_DRAWING', this.drawingOption);
  };

  @action
  setDrawingTextMode = () => {
    this.setDrawingMode('TEXT', this.drawingOption);
  };

  @action
  setFontSize = (size) => {
    this.wrapAndSetTextStyle('fontSize', parseInt(size, 10));
  };

  @action
  setFontColor = (color) => {
    this.wrapAndSetTextStyle('fill', color);
  };

  @action
  toggleAlignLeft = () => {
    this.toggleTextOption('textAlign', 'left');
  };

  @action
  toggleAlignCenter = () => {
    this.toggleTextOption('textAlign', 'center');
  };

  @action
  toggleAlignRight = () => {
    this.toggleTextOption('textAlign', 'right');
  };

  @action
  toggleBold = () => {
    this.toggleTextOption('fontWeight', 'bold');
  };

  @action
  toggleItalic = () => {
    this.toggleTextOption('fontStyle', 'italic');
  };

  @action
  toggleUnderline = () => {
    this.toggleTextOption('textDecoration', 'underline');
  };

  @action
  wrapAndSetTextStyle = (key, value) => {
    pipe(objOf(key), juxt([this.setTextStyle, this.setTextOption]))(value);
  };
}

export default ImageEditorStore;

export const imageEditorStore = new ImageEditorStore();
